import { type HTMLDynamicListElement, ListUpdatedEvent } from "@modules/_components/HTMLDynamicListElement"
import { type TagsListData, TagsUpdateEvent, tagsList, updateTagList } from "@modules/bookmarks/tags/tag-list"
import { InfoPanel } from "@modules/info-panel/InfoPanel"
import { addEventListenerAll, registerGlobalEventListener } from "@modules/util/events"
import { openTagDropdownForSinglePage } from "../tags/tags-dropdown"
import {
    type TagListUserApiItem,
    type TagListUserApiResponse,
    getTagList,
    getUserTagListWithConnectedAdverts,
} from "./bookmark-tag-list"
import type { TagListApiItem, TagListApiResponse } from "./bookmark-tag-list"
import { getAdvertId, sendBookmarksApiRequest, updateBookmarkStateAfterInsertion } from "./bookmarks"
import type { BookmarksApiResponse } from "./bookmarks"
import { newTagLoginRequiredModal } from "./modal-selectors"
import { advertTagsListTemplate, dropdownTagsListTemplate, tagPopupTemplate } from "./templates"
import type { AdvertTagData } from "./templates"

type BookmarkTagsSuccessResponse = {
    success: string
    tagId: string
    count: number
    status: string
    tagAssigned: string
    hasTagAssigned: boolean
}
type BookmarkTagsErrorResponse = {
    error: string
    errorCode: number
}
type BookmarkTagsApiResponse = BookmarkTagsSuccessResponse & BookmarkTagsErrorResponse

type AssignTagSuccessResponse = {
    added: boolean
    msg: string
    removed: boolean
    status: string
}
type AssignTagErrorResponse = {
    status: string
    msg: string
}
type AssignTagApiResponse = AssignTagSuccessResponse & AssignTagErrorResponse

export type HTMLTagsListElement = HTMLBodyElement & {
    tagsList: TagsListData[]
}

export const TagsListUpdatedEvent = "advert.tagsList:updated" as const

const apiBaseUrl = SiteInfo.yiiLadiesUrl
const addTagApiEndpoint = `${apiBaseUrl}/merkzettel/AjaxUpdateNew`
const assignTagApiEndpoint = `${apiBaseUrl}/merkzettel/AjaxUpdateAssignTag`

const isUserLoggedIn = (): boolean => userData.isLoggedIn || false
const bookmarkTagsInfoboxContainer = (): HTMLElement | null =>
    (document.getElementById("contacts-info-container") as HTMLElement) || null
const bookmarkTagsAdvertBoxContainer = (): HTMLElement | null =>
    (bookmarkTagsInfoboxContainer()?.querySelector(".contacts-info-tags-box") as HTMLElement) || null
const bookmarkTagsNavbarContainer = (): HTMLElement | null =>
    (document.querySelector(".navbar-nav li.fake-merken") as HTMLElement) || null
const bookmarkTagButtons = (): Array<HTMLElement> => [
    ...(document.querySelectorAll(
        "#contacts-info-container .merkzettel-tag-button, .advert-actions .merkzettel-tag-button, .navbar-nav .merkzettel-tag-button",
    ) as NodeListOf<HTMLElement>),
]

let tagPopup: HTMLElement | null = null
let tagPopupContainer: HTMLDynamicListElement<AdvertTagData> | null = null
let tagAdvertBox: HTMLDynamicListElement<AdvertTagData> | null = null
let isTagPopupOpen = false
let retryFetchTags = 5

const sortByReferenceTagsList = (unsortedList: TagsListData[]): TagsListData[] => {
    const getTagPosition = (tagSlug: string): number => {
        const position = tagsList.findIndex((referenceTag) => referenceTag.tagSlug === tagSlug)
        return position === -1 ? Number.POSITIVE_INFINITY : position
    }

    return unsortedList.sort((tagA, tagB) => getTagPosition(tagA.tagSlug) - getTagPosition(tagB.tagSlug))
}

export const registerBookmarkTagsEvents = async (): Promise<void> => {
    const tagsListElement = document.body as HTMLTagsListElement

    Object.defineProperty(tagsListElement, "tagsList", {
        get: (): TagsListData[] => {
            const userTags = document.body.dataset.userTags ?? ""

            if (userTags === "") {
                return []
            }

            return JSON.parse(userTags)
        },
        set: (list: TagsListData[]): void => {
            if (!tagsListElement) {
                return
            }

            tagsListElement.dataset.userTags = JSON.stringify(sortByReferenceTagsList(list))
            tagsListElement.dispatchEvent(new CustomEvent(TagsListUpdatedEvent))
        },
    })

    addEventListenerAll(
        bookmarkTagButtons(),
        "click",
        (clickEvent: PointerEvent): void => {
            const tagModalToggleButton = clickEvent.currentTarget as HTMLElement
            const panel: HTMLElement | null = tagModalToggleButton.closest<HTMLElement>(
                ".view-pc #contacts-info-container, .view-mobilenew .relative:has(.merkzettel-tag-button), .advert-actions, .navbar-nav .fake-merken",
            )

            if (panel === null) {
                return
            }

            openTagDropdownForSinglePage(panel)
        },
        { passive: true },
    )

    document.addEventListener(TagsUpdateEvent, (updateEvent) => {
        const advertTags = []
        for (const entry of updateEvent.detail) {
            if (entry.advertIds.indexOf(Number.parseInt(getAdvertId(), 10)) >= 0) {
                advertTags.push({
                    tagSlug: entry.tagSlug,
                    tagName: entry.tagName,
                } as TagsListData)
            }
        }

        tagsListElement.tagsList = advertTags
    })
}

function registerTagPopupEvents(): void {
    addTagContainerElements()

    if (isUserLoggedIn()) {
        fetchTags()
    }

    bookmarkTagButtons().map((bookmarkButton: HTMLElement): void => {
        bookmarkButton?.addEventListener("click", toggleTagPopup, { passive: true })
    })

    const boxContainer: HTMLElement | null = bookmarkTagsAdvertBoxContainer()
    if (boxContainer === null) {
        return
    }

    boxContainer
        .querySelector(".button-contacts-info-tags")
        ?.addEventListener("click", (): void => toggleBoxContainerHeight(boxContainer))
}

function registerTagPopupRetargetEvents(): void {
    registerGlobalEventListener("floating-nav-visibility-changed", (changeEvent: CustomEvent): void => {
        const infoboxContainer: HTMLElement | null = bookmarkTagsInfoboxContainer()
        const navbarContainer: HTMLElement | null = bookmarkTagsNavbarContainer()

        if (!tagPopup || !infoboxContainer || !navbarContainer) {
            return
        }

        if (changeEvent.detail.isVisible()) {
            navbarContainer.appendChild(tagPopup)
        } else {
            infoboxContainer.appendChild(tagPopup)
        }
    })
}

function clearTagAssignments(): void {
    if (tagPopupContainer !== null) {
        const tagsList: AdvertTagData[] = tagPopupContainer.dataList

        for (const tag of tagsList) {
            tag.inactiveTagClass = "hidden"
        }

        updatePopupTagList(tagsList)
    }

    if (tagAdvertBox !== null) {
        updateAdvertTagList([])
    }
}

function handleAddNewTagButtonClick(): void {
    if (!isUserLoggedIn()) {
        $(newTagLoginRequiredModal()).modal("show")
        return
    }

    if (tagPopup === null) {
        return
    }

    tagPopup.classList.add("form-open")
    const tagNameInput: HTMLInputElement | null = tagPopup.querySelector("input") as HTMLInputElement | null
    if (tagNameInput === null) {
        return
    }

    tagNameInput.focus()
}

function closeNewTagForm(): void {
    if (tagPopup === null) {
        return
    }

    tagPopup.classList.remove("form-open")

    const tagNameInput: HTMLInputElement | null = tagPopup.querySelector("input") as HTMLInputElement | null
    if (tagNameInput === null) {
        return
    }

    tagNameInput.value = ""
}

function handleAddNewTagSaveClick(inputElement: HTMLInputElement | null): void {
    if (!isUserLoggedIn()) {
        $(newTagLoginRequiredModal()).modal("show")
        return
    }

    if (!inputElement) {
        return
    }

    if (inputElement.value === "") {
        displayErrorInfo("Der Tag-Name darf nicht leer sein.")
        return
    }

    const advertId = getAdvertId()
    sendBookmarkTagApiRequest(inputElement.value, advertId).then()
}

function handleTagAssignmentClick(tagElement: HTMLElement): void {
    const dataTag: HTMLElement | null = tagElement.closest("[data-tag-id]") as HTMLElement | null
    if (dataTag === null) {
        return
    }

    const tagId: string = dataTag.dataset.tagId || ""
    const tagName: string = dataTag.dataset.tag || ""
    const advertId: string = getAdvertId()

    if (tagId === "" || tagName === "" || advertId === "") {
        return
    }

    sendAssignTagApiRequest(tagId, tagName, advertId, dataTag)
}

function addTagContainerElements(): void {
    tagPopup ??= (tagPopupTemplate?.cloneNode(true) as HTMLElement) || null
    tagPopupContainer ??= (dropdownTagsListTemplate?.cloneNode(true) as HTMLDynamicListElement<AdvertTagData>) || null
    tagAdvertBox ??= (advertTagsListTemplate?.cloneNode(true) as HTMLDynamicListElement<AdvertTagData>) || null

    if (tagAdvertBox !== null) {
        const boxContainer: HTMLElement | null = bookmarkTagsAdvertBoxContainer()

        boxContainer?.prepend(tagAdvertBox)
        tagAdvertBox.addEventListener("list:updated", (): void => {
            if (boxContainer === null || tagAdvertBox === null) {
                return
            }

            updateTagAdvertBoxOverflow(boxContainer, tagAdvertBox)
        })
    }

    if (tagPopup === null || tagPopupContainer === null) {
        return
    }

    tagPopup.prepend(tagPopupContainer)

    const newTagInput: HTMLInputElement | null = tagPopup.querySelector("input") as HTMLInputElement | null

    tagPopup.querySelector("#context-menu-new-tag-form-close")?.addEventListener("click", (): void => closeNewTagForm())
    tagPopup
        .querySelector("#merkzettel-new-tag-trigger")
        ?.addEventListener("click", (): void => handleAddNewTagButtonClick())
    tagPopup.querySelector("#merkzettel-new-tag-save")?.addEventListener("click", (clickEvent: Event): void => {
        clickEvent.preventDefault()
        handleAddNewTagSaveClick(newTagInput)
    })
}

function fetchTags(): void {
    if (tagPopupContainer !== null) {
        tagPopupContainer.classList.add("loading")
    }

    if (SiteInfo.bookmarksPageActive) {
        const userId = userData.userId || "0"
        if (userId === "0") {
            return
        }

        getUserTagListWithConnectedAdverts()
            .then((response: TagListUserApiResponse): void => {
                tagPopupContainer?.classList.remove("loading")
                if (response.entries.length < 1) {
                    tagPopupContainer !== null && tagPopup?.removeChild(tagPopupContainer)
                    return
                }

                requestAnimationFrame(() => {
                    addAdvertTagsToList(response.entries)
                    tagPopupContainer?.classList.remove("loading")
                })
            })
            .catch((error: Error): void => {
                const retryTimeoutSeconds: number = 15

                retryFetchTags > 0
                    ? console.error(
                          "Failed to fetch tags:",
                          error.message,
                          `\nRetrying in ${retryTimeoutSeconds} seconds... (${retryFetchTags} attempts remaining)`,
                      )
                    : (() => {
                          console.error("Failed to fetch tags:", error.message, "\nNo more attempts remaining")
                          tagPopupContainer !== null && tagPopup?.removeChild(tagPopupContainer)
                      })()

                --retryFetchTags >= 0 && setTimeout((): void => fetchTags(), retryTimeoutSeconds * 1000)
            })
        return
    }

    getTagList()
        .then((response: TagListApiResponse): void => {
            tagPopupContainer?.classList.remove("loading")
            if (response.tags.length < 1) {
                tagPopupContainer !== null && tagPopup?.removeChild(tagPopupContainer)
                return
            }

            requestAnimationFrame(() => {
                addTagsToList(response.tags, response.advertTags)
                tagPopupContainer?.classList.remove("loading")
            })
        })
        .catch((error: Error): void => {
            const retryTimeoutSeconds: number = 15

            retryFetchTags > 0
                ? console.error(
                      "Failed to fetch tags:",
                      error.message,
                      `\nRetrying in ${retryTimeoutSeconds} seconds... (${retryFetchTags} attempts remaining)`,
                  )
                : (() => {
                      console.error("Failed to fetch tags:", error.message, "\nNo more attempts remaining")
                      tagPopupContainer !== null && tagPopup?.removeChild(tagPopupContainer)
                  })()

            --retryFetchTags >= 0 && setTimeout((): void => fetchTags(), retryTimeoutSeconds * 1000)
        })
}

function addTagsToList(tags: Array<TagListApiItem>, advertTags: Array<number>): void {
    const tagData: AdvertTagData[] = tags.map((tag: TagListApiItem): AdvertTagData => {
        return {
            tagId: tag.TagID,
            tagName: tag.Tag,
            inactiveTagClass: advertTags.includes(Number.parseInt(tag.TagID, 10)) ? "" : "hidden",
            events: {
                click(clickEvent: MouseEvent): void {
                    handleTagAssignmentClick(clickEvent.target as HTMLElement)
                },
            },
        } as AdvertTagData
    })

    updatePopupTagList(tagData)
    updateAdvertTagList(tagData)
}

function addAdvertTagsToList(entries: Array<TagListUserApiItem>): void {
    const advertId = getAdvertId()

    const tagData: AdvertTagData[] = entries.map((tag: TagListUserApiItem): AdvertTagData => {
        return {
            tagId: tag.tagId,
            tagName: tag.tagName,
            inactiveTagClass: tag.advertIds.includes(Number(advertId)) ? "" : "hidden",
            events: {
                click(clickEvent: MouseEvent): void {
                    handleTagAssignmentClick(clickEvent.target as HTMLElement)
                },
            },
        } as AdvertTagData
    })

    updatePopupTagList(tagData)
    updateAdvertTagList(tagData)
}

function addNewTagToTagList(newTagData: BookmarkTagsSuccessResponse, tagName: string): void {
    if (tagPopupContainer === null) {
        return
    }

    const currentTagList: Array<AdvertTagData> = tagPopupContainer.dataList

    currentTagList.push({
        tagId: newTagData.tagId,
        tagName,
        inactiveTagClass: newTagData.hasTagAssigned ? "" : "hidden",
        events: {
            click(clickEvent: MouseEvent): void {
                handleTagAssignmentClick(clickEvent.target as HTMLElement)
            },
        },
    } as AdvertTagData)

    tagPopupContainer.addEventListener(
        ListUpdatedEvent,
        (): void => {
            if (tagPopupContainer === null || tagPopup === null) {
                return
            }

            const tagItem: HTMLElement | null = tagPopupContainer.querySelector(
                `[data-tag-id="${newTagData.tagId}"]`,
            ) as HTMLElement | null
            tagItem?.scrollIntoView({
                block: "nearest",
                inline: "start",
            })

            closeNewTagForm()
        },
        { passive: true, once: true },
    )

    updateTagList()

    updatePopupTagList(currentTagList)
    updateAdvertTagList(currentTagList)
}

function closeTagPopupOnOutsideClick(clickEvent: MouseEvent): void {
    if (tagPopup?.contains(clickEvent.target as Node)) {
        return
    }

    for (const bookmarkButton of bookmarkTagButtons()) {
        if (bookmarkButton?.contains(clickEvent.target as Node)) {
            return
        }
    }

    closeTagPopup()
}

let currentTagPopupContainer: HTMLElement | null = null

function toggleTagPopup(clickEvent: MouseEvent): void {
    if (tagPopup === null) {
        return
    }

    const eventTarget: HTMLElement | null = clickEvent.target as HTMLElement | null
    const tagPopupContainer: HTMLElement | null = (eventTarget?.closest("button")?.parentElement as HTMLElement) || null
    if (tagPopupContainer === null) {
        return
    }

    if (currentTagPopupContainer === tagPopupContainer && isTagPopupOpen) {
        closeTagPopup()
        return
    }

    currentTagPopupContainer = tagPopupContainer

    tagPopupContainer.appendChild(tagPopup)
    tagPopup.classList.remove("hidden")

    isTagPopupOpen = true
    document.addEventListener("click", closeTagPopupOnOutsideClick, { passive: true })
}

function closeTagPopup(): void {
    if (tagPopup === null) {
        return
    }

    tagPopup.classList.add("hidden")
    closeNewTagForm()

    isTagPopupOpen = false
    document.removeEventListener("click", closeTagPopupOnOutsideClick)
}

function toggleBoxContainerHeight(boxContainer: HTMLElement): void {
    if (tagAdvertBox === null) {
        return
    }

    const isExpanded = boxContainer.classList.contains("expanded")
    if (isExpanded) {
        boxContainer.classList.remove("expanded")
        return
    }

    boxContainer.classList.add("expanded")
}

async function sendBookmarkTagApiRequest(tagName: string, advertId: string): Promise<void> {
    const response = await fetch(addTagApiEndpoint, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: new URLSearchParams({
            tag: tagName,
            auftragsId: advertId,
            assignTag: "true",
        }),
    })

    if (response.status !== 200) {
        displayErrorInfo("Fehler beim Speichern des Tags.")
        return
    }

    const responseData = (await response.json()) as BookmarkTagsApiResponse
    if ("error" in responseData) {
        /** @var {BookmarkTagsErrorResponse} responseData */
        displayErrorInfo(responseData.error)
        return
    }

    updateBookmarkStateAfterInsertion(responseData as BookmarksApiResponse, advertId).then((): void => {
        addNewTagToTagList(responseData, tagName)
    })
}

function sendAssignTagApiRequest(tagId: string, tagName: string, advertId: string, dataTag: HTMLElement): void {
    const requestTagAssignment = async (): Promise<void> => {
        const response: Response = await fetch(assignTagApiEndpoint, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            body: new URLSearchParams({
                id: tagId,
                tag: tagName,
                auftragsId: advertId,
            }),
        })

        const responseData: AssignTagApiResponse = (await response.json()) as AssignTagApiResponse

        if (response.status !== 200) {
            /** @var {AssignTagErrorResponse} responseData */
            displayErrorInfo(`Fehler beim Zuweisen des Tags: ${responseData.msg}`)
            return
        }

        updateAdvertTagAssignment(dataTag, responseData)
    }

    if (siteConfig.merkzettelIds.includes(advertId)) {
        requestTagAssignment().then()
    } else {
        sendBookmarksApiRequest("insert", advertId).then((): void => {
            requestTagAssignment().then()
        })
    }
}

function updateAdvertTagAssignment(dataTag: HTMLElement, responseData: AssignTagSuccessResponse): void {
    const icon: HTMLElement | null = dataTag.querySelector(".icon") as HTMLElement | null
    if (icon === null) {
        return
    }

    if (responseData.added) {
        icon.classList.remove("hidden")
    } else if (responseData.removed) {
        icon.classList.add("hidden")
    }

    if (tagPopupContainer !== null) {
        const updatedTagList: AdvertTagData[] = tagPopupContainer.dataList.map((tag: AdvertTagData): AdvertTagData => {
            if (tag.tagId === dataTag.dataset.tagId) {
                tag.inactiveTagClass = responseData.added ? "" : "hidden"
            }

            return tag
        })

        updatePopupTagList(updatedTagList)
    }

    if (tagAdvertBox !== null) {
        let updatedAdvertTagsDataList: AdvertTagData[] = tagAdvertBox.dataList

        if (responseData.added) {
            if (
                "tagId" in dataTag.dataset &&
                dataTag.dataset.tagId !== undefined &&
                "tag" in dataTag.dataset &&
                dataTag.dataset.tag !== undefined
            ) {
                updatedAdvertTagsDataList.push({
                    tagId: dataTag.dataset.tagId,
                    tagName: dataTag.dataset.tag,
                    inactiveTagClass: "",
                })
            }
        } else if (responseData.removed) {
            updatedAdvertTagsDataList = tagAdvertBox.dataList.filter(
                (tag: AdvertTagData): boolean => tag.tagId !== dataTag.dataset.tagId,
            )
        }

        updateAdvertTagList(updatedAdvertTagsDataList)
    }

    InfoPanel.displayMessage(
        responseData.msg,
        responseData.added || responseData.removed ? InfoPanel.Type.Success : InfoPanel.Type.Error,
        2 * 1000, // expects Milliseconds
    )
}

function updatePopupTagList(tagData: AdvertTagData[]) {
    if (tagPopupContainer === null) {
        return
    }

    tagPopupContainer.dataList = tagData
}

function updateAdvertTagList(tagData: AdvertTagData[]) {
    if (tagAdvertBox === null) {
        return
    }

    const activeTagList = tagData.filter((tag: AdvertTagData): boolean => tag.inactiveTagClass === "")
    tagAdvertBox.dataList = activeTagList

    const boxContainer = bookmarkTagsAdvertBoxContainer()
    if (boxContainer === null) {
        return
    }

    activeTagList.length > 0 ? boxContainer.classList.remove("hidden") : boxContainer.classList.add("hidden")
}

let boxContainerMaxHeight = 0
function updateTagAdvertBoxOverflow(container: HTMLElement, tagsBox: HTMLDynamicListElement<AdvertTagData>): void {
    if (boxContainerMaxHeight <= 0) {
        boxContainerMaxHeight = Math.ceil(container.getBoundingClientRect().height)
    }

    container.style.setProperty("--expanded-height", `${Math.ceil(tagsBox.getBoundingClientRect().height)}px`)

    if (Math.floor(tagsBox.getBoundingClientRect().height) > boxContainerMaxHeight) {
        container.querySelector(".button-contacts-info-tags")?.classList.remove("hidden")
        return
    }

    container.classList.remove("expanded")
    container.querySelector(".button-contacts-info-tags")?.classList.add("hidden")
}

function displayErrorInfo(message: string): void {
    InfoPanel.displayMessage(
        message,
        InfoPanel.Type.Error,
        5 * 1000, // expects Milliseconds
    )
}

export default {
    registerTagPopupEvents,
    registerTagPopupRetargetEvents,
    clearTagAssignments,
}

// Register Custom Events
declare global {
    // noinspection JSUnusedGlobalSymbols
    interface DocumentEventMap {
        [TagsListUpdatedEvent]: CustomEvent
    }
    interface HTMLElementEventMap {
        [TagsListUpdatedEvent]: CustomEvent
    }
}
